import {Injectable} from "@angular/core";
import {AddressModel, addressModelFactory} from "../address/address.model";
import {paramsToQueryString} from "../http/url-params-serializer";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {BuildingModel, buildingModelFactory, buildingModelListFactory} from "./building.model";
import {FloorModel, floorModelListFactory} from "./floor.model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Injectable()
export class BuildingService {
  private url: string = "/api/building";

  constructor(private http: HttpClient) {}

  getBuildings(
    page = DEFAULT_PAGE,
    limit = DEFAULT_PAGE_LIMIT,
    search: string,
    siteId: string,
    projectId: string,
  ): Promise<{rows: BuildingModel[], count: number, floors: FloorModel[]}> {
    const params = {
      page: `${page}`,
      limit: `${limit}`,
      search: `${search || ""}`,
      siteId: `${siteId || ""}`,
      projectId: `${projectId || ""}`,
      sequence: "true",
    };

    return this.http.get<{rows: BuildingModel[], count: number, floors: FloorModel}>(`${this.url}`, {params})
      .pipe(
        map(
          ({rows, count, floors}) =>
            ({rows: buildingModelListFactory(rows), count: +count, floors: floorModelListFactory(floors)}),
        ),
      )
      .toPromise();
  }

  getBuildingsBySiteId(siteId: string): Promise<any[]> {
    return this.http.get<any[]>(`${this.url}/getlistbysite`, {params: {siteId}})
      .toPromise();
  }

  getBuildingById(id: string): Promise<BuildingModel> {
    return this.http.get<{rows: BuildingModel[]}>(`${this.url}`, {params: {id}})
      .pipe(map(json => buildingModelFactory(json.rows[0])))
      .toPromise();
  }

  saveBuilding(building: BuildingModel): Promise<boolean> {
    return this.http.post(`${this.url}/save`, {...building})
      .toPromise()
      .then(() => true);
  }

  async getAddress(id: string, type: string): Promise<AddressModel> {
    const params = paramsToQueryString({id});

    switch (type) {
      case "siteAddress":
        return this.http.get<{siteAddress: AddressModel}>(`${this.url}/address?${params}`)
          .pipe(map(({siteAddress}) => addressModelFactory(siteAddress)))
          .toPromise();
      case "tonerAddress":
        return this.http.get<{tonerAddress: AddressModel}>(`${this.url}/address-toner?${params}`)
          .pipe(map(({tonerAddress}) => addressModelFactory(tonerAddress)))
          .toPromise();
      case "serviceAddress":
        return this.http.get<{serviceAddress: AddressModel}>(`${this.url}/address-service?${params}`)
          .pipe(map(({serviceAddress}) => addressModelFactory(serviceAddress)))
          .toPromise();
      default:
        throw new Error("Unknown address type.");
    }
  }

  setAddress(id: string, buildingAddress: AddressModel, type: string): Promise<AddressModel> {
    switch (type) {
      case "siteAddress":
        return this.http.post<AddressModel>(`${this.url}/address`, {id, buildingAddress})
          .pipe(map(it => addressModelFactory(it)))
          .toPromise();
      case "tonerAddress":
        return this.http.post<AddressModel>(`${this.url}/address-toner`, {id, buildingAddress})
          .pipe(map(it => addressModelFactory(it)))
          .toPromise();
      case "serviceAddress":
        return this.http.post<AddressModel>(`${this.url}/address-service`, {id, buildingAddress})
          .pipe(map(it => addressModelFactory(it)))
          .toPromise();
      default: {
        throw new Error("Unknown address type.");
      }
    }
  }

  remove(siteId: string, ids): Promise<boolean> {
    return this.http.get(`${this.url}/remove`, {params: {siteId, ids}})
      .toPromise()
      .then(() => true);
  }
}
